// Gemeinsame Bausteine der Widget-Spalte (Handoff §6): Feldstile, Rund-Knöpfe, Objekt-/Mieter-Suche
// und das Nachladen der Namen (Ersteller, Vorschläge, Einheit). Abfragen 1:1 aus TicketEigenschaften.
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import type { Icon } from '@phosphor-icons/react';
import { supabase } from '../../../../lib/supabase/client';
import type { Ticket } from '../types';
import { ladeEigentuemerIds } from '../../../../lib/tickets/ticketEigentuemer';

export const LABEL: CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--ink3)' };

export const FELD: CSSProperties = {
  height: 40, width: '100%', minWidth: 0, padding: '0 12px', borderRadius: 12, border: 'none', outline: 'none',
  background: 'var(--fill2)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
};

/** Kleiner runder Icon-Knopf (Stift, Pfeil, ×). */
export function RundKlein({ icon: I, label, tip, onClick, farbe, groesse = 30 }: {
  icon: Icon; label: string; tip?: string; onClick: () => void; farbe?: string; groesse?: 24 | 26 | 30;
}) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} aria-label={label} data-tip={tip ?? label}
      className="inline-flex shrink-0 items-center justify-center"
      style={{ width: groesse, height: groesse, borderRadius: '50%', border: 'none', cursor: 'pointer',
        background: farbe ? `color-mix(in srgb, ${farbe} 15%, transparent)` : 'var(--fill2)', color: farbe ?? 'var(--ink2)' }}>
      <I size={groesse < 30 ? 10 : 13} weight="bold" />
    </button>
  );
}

/** Klein-Knopf „Fertig“ / „Abbrechen“ neben Inline-Auswahlen. */
export function FertigKnopf({ onClick, children = 'Fertig', tip = 'Fertig – Auswahl schließen' }: { onClick: () => void; children?: ReactNode; tip?: string }) {
  return (
    <button type="button" onClick={onClick} data-tip={tip}
      style={{ height: 36, padding: '0 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'var(--bg)', color: 'var(--ink)',
        fontFamily: 'inherit', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
      {children}
    </button>
  );
}

export interface SuchTreffer { id: string; titel: string; unter?: string | null; kuerzel?: string | null }

/** Objekt-/Mieter-Suche (ab 2 Zeichen, max. 6 Treffer) – Abfragen wie bisher im Bearbeiten-Modus. */
export function SuchAuswahl({ art, onWahl, autoFokus = false, vorschlaege = [], imFluss = false }: {
  art: 'objekt' | 'mieter' | 'eigentuemer'; onWahl: (t: SuchTreffer) => void; autoFokus?: boolean;
  /** Vorschläge, solange nichts gesucht wird (#513: Eigentümer des Objekts) */
  vorschlaege?: SuchTreffer[];
  /** Trefferliste im Textfluss statt schwebend – in Widgets, deren Hülle überstehende Inhalte abschneidet (#513 Bild 3) */
  imFluss?: boolean;
}) {
  const [text, setText] = useState('');
  const [treffer, setTreffer] = useState<SuchTreffer[]>([]);

  const suche = async (q: string) => {
    setText(q);
    if (q.trim().length < 2) { setTreffer([]); return; }
    if (art === 'objekt') {
      const { data } = await supabase.from('objekte')
        .select('id, bezeichnung, strasse, hausnummer, objektnummer')
        .or(`bezeichnung.ilike.%${q}%,strasse.ilike.%${q}%,objektnummer.ilike.%${q}%`)
        .limit(6);
      setTreffer((data || []).map((o: { id: string; bezeichnung: string | null; strasse: string | null; hausnummer: string | null; objektnummer: string | null }) => ({
        id: o.id, titel: o.bezeichnung || 'Ohne Bezeichnung', unter: [o.strasse, o.hausnummer].filter(Boolean).join(' '), kuerzel: o.objektnummer,
      })));
    } else if (art === 'eigentuemer') {
      const { data } = await supabase.from('eigentuemer')
        .select('id, vorname, nachname, firma_name, email')
        .or(`vorname.ilike.%${q}%,nachname.ilike.%${q}%,firma_name.ilike.%${q}%`)
        .limit(6);
      setTreffer((data || []).map((e: { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null }) => ({
        id: e.id, titel: (e.firma_name ?? '').trim() || `${e.vorname ?? ''} ${e.nachname ?? ''}`.trim() || 'Ohne Namen', unter: e.email,
      })));
    } else {
      const { data } = await supabase.from('mieter')
        .select('id, vorname, nachname, email')
        .or(`vorname.ilike.%${q}%,nachname.ilike.%${q}%`)
        .limit(6);
      setTreffer((data || []).map((m: { id: string; vorname: string | null; nachname: string | null; email: string | null }) => ({
        id: m.id, titel: `${m.vorname ?? ''} ${m.nachname ?? ''}`.trim() || 'Ohne Namen', unter: m.email,
      })));
    }
  };

  return (
    <div className="relative min-w-0 flex-1">
      <input value={text} onChange={(e) => void suche(e.target.value)} autoFocus={autoFokus}
        placeholder={art === 'objekt' ? 'Objekt suchen (Bezeichnung, Straße, Nr.) …' : art === 'eigentuemer' ? 'Eigentümer suchen (Name, Firma) …' : 'Mieter suchen (Vor-/Nachname) …'}
        aria-label={art === 'objekt' ? 'Objekt suchen' : art === 'eigentuemer' ? 'Eigentümer suchen' : 'Mieter suchen'}
        data-tip={art === 'objekt' ? 'Objekt suchen – Nach Nummer oder Adresse' : art === 'eigentuemer' ? 'Eigentümer – Betroffener oder meldender Eigentümer' : 'Mieter – Meldender oder betroffener Mieter'}
        style={{ ...FELD, height: 36, background: 'var(--bg)', fontSize: 13 }} />
      {(treffer.length > 0 || (text.trim().length < 2 && vorschlaege.length > 0)) && (
        <div role="listbox" className={`immo-scroll flex flex-col overflow-y-auto ${imFluss ? '' : 'absolute left-0 right-0 z-20'}`}
          style={{ ...(imFluss ? { marginTop: 4 } : { top: 'calc(100% + 4px)' }), maxHeight: 200, padding: 4, borderRadius: 14, background: 'var(--bg)', boxShadow: '0 18px 40px -12px var(--shade), inset 0 0 0 1px var(--line)' }}>
          {(treffer.length > 0 ? treffer : vorschlaege).map((t) => (
            <button key={t.id} type="button" role="option" aria-selected={false}
              onClick={() => { onWahl(t); setText(''); setTreffer([]); }}
              className="immo-menue-zeile flex min-w-0 flex-col text-left"
              style={{ padding: '7px 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
              <span className="flex min-w-0 items-center" style={{ gap: 6 }}>
                {t.kuerzel && <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--ink3)' }}>{t.kuerzel}</span>}
                <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{t.titel}</span>
              </span>
              {t.unter && <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{t.unter}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Namen, die das Ticket nur als id kennt (on demand nachgeladen, Muster wie TicketEigenschaften). */
export function useTicketNamen(ticket: Ticket) {
  const [ersteller, setErsteller] = useState('');
  const [dienstleisterVorschlag, setDienstleisterVorschlag] = useState<string | null>(null);
  const [bearbeiterVorschlag, setBearbeiterVorschlag] = useState<string | null>(null);
  const [einheit, setEinheit] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    if (ticket.erstellt_von) {
      supabase.from('profiles').select('full_name').eq('id', ticket.erstellt_von).single()
        .then(({ data }) => { if (!weg) setErsteller((data?.full_name as string) || 'Unbekannt'); });
    } else setErsteller('');
    return () => { weg = true; };
  }, [ticket.erstellt_von]);

  useEffect(() => {
    let weg = false;
    if (ticket.dienstleister_vorschlag_id) {
      supabase.from('dienstleister').select('name').eq('id', ticket.dienstleister_vorschlag_id).maybeSingle()
        .then(({ data }) => { if (!weg) setDienstleisterVorschlag((data?.name as string) ?? null); });
    } else setDienstleisterVorschlag(null);
    return () => { weg = true; };
  }, [ticket.dienstleister_vorschlag_id]);

  useEffect(() => {
    let weg = false;
    const id = ticket.bearbeiter_vorschlag_id;
    if (id && ticket.bearbeiter_vorschlag_typ === 'team') {
      supabase.from('teams').select('name').eq('id', id).maybeSingle()
        .then(({ data }) => { if (!weg) setBearbeiterVorschlag((data?.name as string) ?? null); });
    } else if (id && ticket.bearbeiter_vorschlag_typ === 'profile') {
      supabase.from('profiles').select('full_name').eq('id', id).maybeSingle()
        .then(({ data }) => { if (!weg) setBearbeiterVorschlag((data?.full_name as string) ?? null); });
    } else setBearbeiterVorschlag(null);
    return () => { weg = true; };
  }, [ticket.bearbeiter_vorschlag_id, ticket.bearbeiter_vorschlag_typ]);

  useEffect(() => {
    let weg = false;
    if (ticket.einheit_id) {
      supabase.from('objekt_einheiten').select('bezeichnung').eq('id', ticket.einheit_id).maybeSingle()
        .then(({ data }) => { if (!weg) setEinheit((data?.bezeichnung as string) ?? null); });
    } else setEinheit(null);
    return () => { weg = true; };
  }, [ticket.einheit_id]);

  return { ersteller, dienstleisterVorschlag, bearbeiterVorschlag, einheit };
}

/** Datum DD.MM.YYYY aus YYYY-MM-DD bzw. ISO. */
export const datumKurz = (d: string) => new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** #513: Eigentümer des Objekts bzw. der Einheit als Vorschläge für die Eigentümer-Auswahl (lädt erst, wenn `aktiv`). */
export function useEigentuemerVorschlaege(objektId: string | null | undefined, einheitId: string | null | undefined, aktiv: boolean): SuchTreffer[] {
  const [liste, setListe] = useState<SuchTreffer[]>([]);
  useEffect(() => {
    if (!aktiv || !objektId) { setListe([]); return; }
    let weg = false;
    void (async () => {
      const ids = await ladeEigentuemerIds(objektId, einheitId);
      if (!ids.length) { if (!weg) setListe([]); return; }
      const { data } = await supabase.from('eigentuemer').select('id, vorname, nachname, firma_name, email').in('id', ids.slice(0, 50));
      if (weg) return;
      setListe(((data || []) as { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; email: string | null }[])
        .map(e => ({ id: e.id, titel: (e.firma_name ?? '').trim() || `${e.vorname ?? ''} ${e.nachname ?? ''}`.trim() || 'Ohne Namen', unter: e.email ? `Eigentümer im Objekt · ${e.email}` : 'Eigentümer im Objekt' }))
        .sort((a, b) => a.titel.localeCompare(b.titel, 'de')));
    })();
    return () => { weg = true; };
  }, [objektId, einheitId, aktiv]);
  return liste;
}
